import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { CONNECTORS_PAGE } from '../../../../lib/use-connectors';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { useTranslation } from '../../../../lib/i18n';
import { TextButton } from '../../ui';
import styles from './mobile.module.css';

/** How many cards peek in the collapsed deck. */
const COLLAPSED_COUNT = 3;
/** The fan geometry (Figma 13131-79009): each card 46px lower and 10px narrower than the one behind. */
const PEEK = 46;
const INSET = 10;
/** View All ⇄ View Less morph. */
const DURATION = 320;
const EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

export interface CardStackItem {
  key: string;
  /** The card. A function form is passed `peek: true` while the card is a collapsed peek in the fan,
   *  so a card that expands (e.g. a connected connector's account list) can render compact in the pile
   *  and full once "View All" flattens it. A plain node ignores the deck state. */
  node: ReactNode | ((opts: { peek: boolean }) => ReactNode);
}

/**
 * A titled deck of overlapping cards that flattens on "View All" (Figma 13131-79009). Collapsed, the
 * back cards are narrower, inset and offset up so only their header row peeks above the full front
 * card; expanded, they lay out as a plain vertical list. Shared by the Connected tools stack and the
 * MCP servers list so both aggregate identically — the caller supplies the pre-rendered cards.
 *
 * The deck appears as soon as there's a second card to stack behind — a lone card has nothing to
 * aggregate, so it renders flat with no "View All". Collapsed, the deck peeks at most `collapsedCount`
 * cards; "View All" flattens to the full list (whether that's 3 or 30).
 *
 * The toggle is animated with FLIP: the cards present in both states morph from their stacked to their
 * flat positions, cards that only exist expanded fade in, and the container height eases so the section
 * below slides instead of jumping. Skipped under `prefers-reduced-motion`.
 */
export function CardStack({
  title,
  count,
  items,
  biToggle,
  collapsedCount = COLLAPSED_COUNT,
  initiallyExpanded = false,
}: {
  title: string;
  count: number;
  items: CardStackItem[];
  /** BI button-name stem: logs `<biToggle>_view_all` / `<biToggle>_view_less`. */
  biToggle: string;
  collapsedCount?: number;
  initiallyExpanded?: boolean;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const [expanded, setExpanded] = useState(initiallyExpanded);

  const collapsible = items.length > 1;
  const stacked = collapsible && !expanded;
  const shown = stacked ? items.slice(0, collapsedCount) : items;
  const last = shown.length - 1;

  const bodyRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef(new Map<string, HTMLElement>());
  const firstRects = useRef<Map<string, DOMRect> | null>(null);
  const firstHeight = useRef<number | null>(null);
  const anims = useRef<Animation[]>([]);

  const toggle = () => {
    logPageClickBi({ ...CONNECTORS_PAGE, button_name: expanded ? `${biToggle}_view_less` : `${biToggle}_view_all` });
    // FLIP "first": measure the current layout before React swaps it.
    if (bodyRef.current) {
      firstHeight.current = bodyRef.current.getBoundingClientRect().height;
      const rects = new Map<string, DOMRect>();
      cardRefs.current.forEach((el, key) => rects.set(key, el.getBoundingClientRect()));
      firstRects.current = rects;
    }
    setExpanded((v) => !v);
  };

  useLayoutEffect(() => {
    const firsts = firstRects.current;
    firstRects.current = null;
    const firstH = firstHeight.current;
    firstHeight.current = null;
    if (!firsts) return; // first render — nothing to animate from

    anims.current.forEach((a) => a.cancel());
    anims.current = [];

    const body = bodyRef.current;
    if (body) {
      body.style.height = '';
      body.style.overflow = '';
    }

    if (typeof window === 'undefined' || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    // Ease the container height so the section below slides; clip while it does so the revealing cards
    // don't spill past the animating edge.
    if (body && firstH != null) {
      const lastH = body.getBoundingClientRect().height;
      if (Math.abs(lastH - firstH) > 1) {
        body.style.overflow = 'hidden';
        const heightAnim = body.animate([{ height: `${firstH}px` }, { height: `${lastH}px` }], { duration: DURATION, easing: EASING });
        const restore = () => {
          body.style.overflow = '';
          body.style.height = '';
        };
        heightAnim.onfinish = restore;
        heightAnim.oncancel = restore;
        anims.current.push(heightAnim);
      }
    }

    cardRefs.current.forEach((el, key) => {
      const firstRect = firsts.get(key);
      if (firstRect) {
        const lastRect = el.getBoundingClientRect();
        const dx = firstRect.left - lastRect.left;
        const dy = firstRect.top - lastRect.top;
        const sx = firstRect.width && lastRect.width ? firstRect.width / lastRect.width : 1;
        if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5 || Math.abs(sx - 1) > 0.005) {
          anims.current.push(
            el.animate(
              [
                { transformOrigin: 'top left', transform: `translate(${dx}px, ${dy}px) scaleX(${sx})` },
                { transformOrigin: 'top left', transform: 'none' },
              ],
              { duration: DURATION, easing: EASING },
            ),
          );
        }
      } else {
        // A card that only exists once expanded — fade it in rather than pop.
        anims.current.push(
          el.animate([{ opacity: 0, transform: 'translateY(-6px)' }, { opacity: 1, transform: 'none' }], {
            duration: DURATION,
            easing: EASING,
          }),
        );
      }
    });
  }, [expanded]);

  // Back cards float behind the front (relative) card, inset and offset up so only their header peeks.
  const fanStyle = (i: number): React.CSSProperties =>
    i < last
      ? { position: 'absolute', top: i * PEEK, left: (last - i) * INSET, right: (last - i) * INSET, zIndex: i }
      : { position: 'relative', marginTop: last * PEEK, zIndex: last };

  const setCardRef = (key: string) => (el: HTMLElement | null) => {
    if (el) cardRefs.current.set(key, el);
    else cardRefs.current.delete(key);
  };

  return (
    <>
      <div className={styles.sectionHead}>
        <h3 className={styles.sectionTitle}>
          {title}
          <span className={styles.sectionCount}>{` · ${count}`}</span>
        </h3>
        {collapsible && (
          <TextButton
            size="tiny"
            endIcon={<ChevronDown size={16} aria-hidden className={expanded ? styles.chevronOpen : undefined} />}
            onClick={toggle}
          >
            {expanded ? t('connectors.viewLess') : t('connectors.viewAll')}
          </TextButton>
        )}
      </div>

      <div ref={bodyRef} className={stacked ? styles.stackFan : styles.stackOpen}>
        {shown.map((item, i) => (
          <div key={item.key} ref={setCardRef(item.key)} style={stacked ? fanStyle(i) : undefined}>
            {typeof item.node === 'function' ? item.node({ peek: stacked }) : item.node}
          </div>
        ))}
      </div>
    </>
  );
}
